import type { RadarRow } from "../api";
import { SPENT, toneFor } from "./tone";

/**
 * A contractual clock, drawn as the thing it is.
 *
 * Every NEC4 clock here runs the same shape: something triggers it, a reply
 * period runs, the other party may give notice of the failure, a further
 * period runs, and then an outcome happens on its own. Five facts. The table
 * used to spread them across a coloured pill, a day count and a detail page,
 * and none of them showed how far through the clock actually was.
 *
 * The colour rule is borrowed from process-control alarm management, where
 * the display is greyscale and colour appears only where an operator must
 * act. Applied here: the track and its stations are always neutral, and the
 * single "now" mark is the only thing that can carry colour — red when this
 * clock still needs somebody and time is short, amber while a further period
 * runs, and nothing at all once the outcome has happened, because a right
 * that is already lost has no response to prompt.
 */
export function ClockBand({ row, today }: { row: RadarRow; today: string }) {
  const plan = layout(row, today);
  if (!plan) {
    return <div className="band band--undated"><span className="k">no dated period</span></div>;
  }

  const { stations, nowAt, tone, spent } = plan;
  return (
    <div className={`band band--${tone}`}>
      <div className="band__track" aria-hidden="true">
        <span className="band__elapsed" style={{ width: `${spent}%` }} />
        {stations.map((s) => (
          <span key={s.key} className={`band__station band__station--${s.key}`}
            style={{ left: `${s.at}%` }} title={`${s.label} — ${s.date}`} />
        ))}
        {nowAt !== null && (
          <span className="band__now" style={{ left: `${nowAt}%` }} />
        )}
      </div>
      {/* Two labels, not four: the intermediate stations overlapped into
          mush at column width. They keep their tick and their tooltip. */}
      <div className="band__legend">
        <span className="band__tick band__tick--start">{stations[0]?.short}</span>
        {stations.length > 1 && (
          <span className="band__tick band__tick--end">
            {stations[stations.length - 1]?.short}
          </span>
        )}
      </div>
    </div>
  );
}

interface Station { key: string; at: number; label: string; short: string; date: string }

function layout(row: RadarRow, today: string) {
  const start = row.trigger_date;
  // The right-hand end is whatever the clock is running towards: the
  // automatic outcome if one is dated, otherwise the deadline in front of it.
  const end = row.deemed_date ?? row.next_deadline ?? row.reply_due_date;
  if (!start || !end) return null;

  const t0 = Date.parse(start);
  const t1 = Date.parse(end);
  if (!Number.isFinite(t0) || !Number.isFinite(t1) || t1 <= t0) return null;

  const at = (iso: string | null) => {
    if (!iso) return null;
    const t = Date.parse(iso);
    if (!Number.isFinite(t)) return null;
    return Math.max(0, Math.min(100, ((t - t0) / (t1 - t0)) * 100));
  };

  const stations: Station[] = [];
  const add = (key: string, iso: string | null, label: string, short: string) => {
    const pos = at(iso);
    if (pos !== null) stations.push({ key, at: pos, label, short, date: iso! });
  };
  add("trigger", start, "Clock started", "started");
  add("reply", row.reply_due_date, "Reply period ends", "reply due");
  add("reminder", row.reminder_notice_date, "Notice of failure given", "notice");
  add("outcome", row.deemed_date, "Automatic outcome", "outcome");

  const nowAt = at(today);
  const spent = nowAt === null ? 100 : nowAt;
  const resolved = SPENT.has(row.state);
  // Colour lives here and only here.
  const tone = toneFor(row);

  return { stations, nowAt: resolved ? null : nowAt, tone, spent };
}
